import React from "react";
import { useLang } from "@/App";

const THERAPIST_PHOTO = "https://customer-assets.emergentagent.com/job_calm-scheduler-2/artifacts/zkiaphz7_image0.png";
const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_calm-scheduler-2/artifacts/4dm8lgvs_image.png";

const NeuroLogo = () => (
  <img
    src={BRAND_LOGO}
    alt="Antrenor de creier · Brain Trainer — Silvana Capris"
    data-testid="nav-logo"
    width={56}
    height={56}
    style={{ width: 56, height: 56, objectFit: "contain", display: "block" }}
  />
);

/**
 * Nav
 * - inPage (default true): anchor links scroll within the current page.
 *   When false (service pages), anchor links navigate to homeHref + #id.
 * - homeHref: base path of the matching home page ("/" or "/en").
 * - langUrls: when provided ({ ro, en }), the language toggle navigates to
 *   the counterpart URL instead of switching language in place.
 */
export default function Nav({ inPage = true, homeHref = "/", langUrls = null }) {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = React.useState(false);

  const go = (id) => {
    setOpen(false);
    if (inPage) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.location.href = `${homeHref}#${id}`;
  };

  const links = [
    { id: "about", label: t.nav.about, testid: "nav-about" },
    { id: "services", label: t.nav.services, testid: "nav-services" },
    { id: "reviews", label: t.nav.reviews, testid: "nav-reviews" },
    { id: "faq", label: t.nav.explore, testid: "nav-explore" },
    { id: "book", label: t.nav.book, testid: "nav-book" },
    { id: "footer", label: t.nav.contact, testid: "nav-contact" },
  ];

  const langBtnStyle = (active) => ({
    padding: "5px 12px",
    borderRadius: 999,
    background: active ? "#0e0e0e" : "transparent",
    color: active ? "#fbf8f1" : "#1a1a1a",
    transition: "all .2s",
    display: "inline-block",
    textDecoration: "none",
  });

  return (
    <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md" style={{ background: "rgba(251,248,241,0.78)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-x flex items-center" style={{ height: 84 }}>
        <a href={homeHref} className="flex items-center gap-3 flex-shrink-0" data-testid="nav-home-link">
          <NeuroLogo />
          <span className="serif" style={{ fontSize: 22, letterSpacing: "0.01em" }}>{t.brand}</span>
        </a>

        <nav className="hidden xl:flex items-center justify-center flex-1 gap-5" style={{ margin: "0 56px" }}>
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="nav-link" data-testid={l.testid}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0 ml-auto xl:ml-0">
          <div className="flex items-center gap-1">
            {langUrls ? (
              <>
                <a href={langUrls.en} data-testid="lang-en" className="text-xs font-medium" style={langBtnStyle(lang === "en")}>EN</a>
                <a href={langUrls.ro} data-testid="lang-ro" className="text-xs font-medium" style={langBtnStyle(lang === "ro")}>RO</a>
              </>
            ) : (
              <>
                <button onClick={() => setLang("en")} data-testid="lang-en" className="text-xs font-medium" style={langBtnStyle(lang === "en")}>EN</button>
                <button onClick={() => setLang("ro")} data-testid="lang-ro" className="text-xs font-medium" style={langBtnStyle(lang === "ro")}>RO</button>
              </>
            )}
          </div>
          <button
            className="xl:hidden ml-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              {open ? <path d="M6 6l12 12M6 18L18 6" /> : <><path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" /></>}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t" style={{ borderColor: "var(--line)", background: "rgba(251,248,241,0.97)" }} data-testid="nav-mobile-panel">
          <div className="container-x py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button key={l.id} onClick={() => go(l.id)} className="text-left py-2 nav-link" data-testid={`m-${l.testid}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

export { THERAPIST_PHOTO };
